import { z } from "zod";

// Funções de validação CPF e CNPJ (assumindo que são as mesmas da organization-schema)
const validateCPF = (cpf: string) => {
  cpf = cpf.replace(/[^\d]+/g, "");
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  let soma = 0,
    resto;
  for (let i = 1; i <= 9; i++)
    soma += Number.parseInt(cpf.charAt(i - 1)) * (11 - i);
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== Number.parseInt(cpf.charAt(9))) return false;
  soma = 0;
  for (let i = 1; i <= 10; i++)
    soma += Number.parseInt(cpf.charAt(i - 1)) * (12 - i);
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  return resto === Number.parseInt(cpf.charAt(10));
};

const validateCNPJ = (cnpj: string) => {
  cnpj = cnpj.replace(/[^\d]+/g, "");
  if (cnpj.length !== 14) return false;
  if (
    cnpj === "00000000000000" ||
    cnpj === "11111111111111" ||
    cnpj === "22222222222222" ||
    cnpj === "33333333333333" ||
    cnpj === "44444444444444" ||
    cnpj === "55555555555555" ||
    cnpj === "66666666666666" ||
    cnpj === "77777777777777" ||
    cnpj === "88888888888888" ||
    cnpj === "99999999999999"
  )
    return false;
  let tamanho = cnpj.length - 2;
  let numeros = cnpj.substring(0, tamanho);
  const digitos = cnpj.substring(tamanho);
  let soma = 0;
  let pos = tamanho - 7;
  for (let i = tamanho; i >= 1; i--) {
    soma += Number.parseInt(numeros.charAt(tamanho - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
  if (resultado !== Number.parseInt(digitos.charAt(0))) return false;
  tamanho++;
  numeros = cnpj.substring(0, tamanho);
  soma = 0;
  pos = tamanho - 7;
  for (let i = tamanho; i >= 1; i--) {
    soma += Number.parseInt(numeros.charAt(tamanho - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
  return resultado === Number.parseInt(digitos.charAt(1));
};

export const customerFormSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  document: z
    .string()
    .optional()
    .refine(
      (val) =>
        !val || val.trim() === "" || validateCPF(val) || validateCNPJ(val),
      "CPF ou CNPJ inválido"
    )
    .or(z.literal("")),
  dateOfBirth: z.date().optional(),
  tags: z.array(z.string()).optional(),
  phone: z
    .object({
      internationalCode: z.string().optional(),
      ddd: z.string().optional().or(z.literal("")),
      phoneNumber: z.string().optional().or(z.literal("")),
    })
    .optional()
    .superRefine((data, ctx) => {
      if (!data) {
        return;
      }
      const { internationalCode, ddd, phoneNumber } = data;
      const dddProvided = ddd && ddd.trim() !== "";
      const phoneNumberProvided = phoneNumber && phoneNumber.trim() !== "";

      if (internationalCode && (dddProvided || phoneNumberProvided)) {
        if (!dddProvided) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "DDD é obrigatório.",
            path: ["ddd"],
          });
        }
        if (!phoneNumberProvided) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Número é obrigatório.",
            path: ["phoneNumber"],
          });
        }
      }

      if (dddProvided && phoneNumberProvided) {
        const dddDigits = ddd.replace(/\D/g, "");
        const phoneNumberDigits = phoneNumber.replace(/\D/g, "");

        if (internationalCode === "55") {
          if (dddDigits.length !== 3) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "DDD para Brasil deve ter 2 dígitos.",
              path: ["ddd"],
            });
          }
          if (phoneNumberDigits.length < 8 || phoneNumberDigits.length > 9) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Número para Brasil deve ter 8 ou 9 dígitos.",
              path: ["phoneNumber"],
            });
          }
        } else if (internationalCode === "1") {
          if (dddDigits.length !== 3) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "DDD (código de área) para EUA deve ter 3 dígitos.",
              path: ["ddd"],
            });
          }
          if (phoneNumberDigits.length !== 7) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Número para EUA deve ter 7 dígitos.",
              path: ["phoneNumber"],
            });
          }
        }
      }
    }),
  addresses: z
    .array(
      z.object({
        zipCode: z.string().optional().or(z.literal("")),
        city: z.string().min(1, "Cidade é obrigatória"),
        neighborhood: z.string().min(1, "Bairro é obrigatório"),
        line1: z.string().min(1, "Endereço é obrigatório"),
        line2: z.string().optional().or(z.literal("")),
        number: z.string().min(1, "Número é obrigatório"),
      })
    )
    .optional(),
  email: z
    .string()
    .optional()
    .refine(
      (val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
      "E-mail inválido"
    )
    .or(z.literal("")),
});

export type CustomerFormSchemaType = z.infer<typeof customerFormSchema>;



